import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { ConcentricRings } from '@/components/Motifs';
import { Button } from '@/components/ui/button';
import { usePageTitle } from '@/hooks/usePageTitle';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.7, ease: [0.22, 1, 0.36, 1] } })
};

export default function GuidancePage() {
  usePageTitle('Guidance');

  const offerings = [
    {
      label: 'Flagship Offering',
      title: 'The Idea Clarity Session',
      tagline: 'One focused conversation. Uncommon clarity.',
      desc: 'A 90-minute working session for the visionary who has a big idea but no framework that fits the way her mind works. You leave with a clear articulation, your core problem, your native first move, and a framework built with you.',
      price: '$247',
      duration: '90 min \u00b7 1:1 \u00b7 Virtual',
      href: '/idea-clarity-session',
      cta: 'Learn More',
      active: true
    },
    {
      label: 'Coming Soon',
      title: 'Strategic Synthesis',
      tagline: 'Ongoing guidance for the builder in motion.',
      desc: 'For founders and leaders navigating complex identity shifts, organizational thresholds, and the architecture of what comes next. Extended engagement, structured around your specific system.',
      href: null,
      cta: null,
      active: false
    },
    {
      label: 'Coming Soon',
      title: 'The Observatory Intensive',
      tagline: 'Deep-dive retreat for systemic clarity.',
      desc: 'A multi-session intensive for those who need more than a single conversation. Designed for founders at critical thresholds, teams navigating transformation, or thinkers building a body of work.',
      href: null,
      cta: null,
      active: false
    }
  ];

  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* Hero */}
      <section className="hero-gradient relative py-20 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1658237775407-aed6450f5851?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85')` }} />
        <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.12 } } }}>
            <motion.p variants={fadeUp} className="font-['Inter'] text-[11px] font-medium uppercase tracking-[0.2em] text-[hsl(var(--silver))] mb-6">
              Services & Sessions
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-[60px] font-bold leading-[1.08] text-[hsl(var(--foreground))] mb-6">
              Guidance
            </motion.h1>
            <motion.p variants={fadeUp} className="font-['Inter'] text-base sm:text-lg leading-[1.8] text-[hsl(var(--muted-foreground))] max-w-xl mx-auto">
              Structured sessions and strategic guidance for founders, leaders, and thinkers navigating the architecture of what they are building.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Offerings */}
      <section className="py-16 sm:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-8">
          {offerings.map((offer, i) => (
            <motion.div key={offer.title} initial="hidden" whileInView="visible" viewport={{ once: true }} custom={i} variants={fadeUp}>
              <div className={`rounded-xl border p-6 sm:p-8 transition-all duration-300 ${
                offer.active
                  ? 'bg-[hsl(var(--card))] border-[rgba(201,168,76,0.25)] shadow-[0_18px_60px_rgba(0,0,0,0.35)] hover:border-[rgba(201,168,76,0.4)]'
                  : 'bg-[hsl(var(--card))] border-[rgba(168,181,178,0.1)] opacity-75'
              }`}>
                <div className="flex items-center justify-between mb-4">
                  <p className={`font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] ${
                    offer.active ? 'text-[hsl(var(--gold))]' : 'text-[hsl(var(--silver))]'
                  }`}>
                    {offer.label}
                  </p>
                  {offer.price && (
                    <p className="font-['Cormorant_Garamond'] text-xl font-bold text-[hsl(var(--gold))]">{offer.price}</p>
                  )}
                </div>
                <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[hsl(var(--foreground))] mb-2 leading-[1.15]">
                  {offer.title}
                </h3>
                {offer.tagline && (
                  <p className="font-['Cormorant_Garamond'] text-lg italic text-[hsl(var(--champagne))] mb-4">{offer.tagline}</p>
                )}
                {offer.duration && (
                  <p className="font-['Inter'] text-xs text-[hsl(var(--silver))] mb-4">{offer.duration}</p>
                )}
                <p className="font-['Inter'] text-[15px] leading-[1.7] text-[hsl(var(--muted-foreground))] mb-6">{offer.desc}</p>
                {offer.href && (
                  <Link to={offer.href} className="inline-flex items-center gap-2 font-['Inter'] text-sm font-medium text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors">
                    {offer.cta} <ArrowRight className="h-4 w-4" />
                  </Link>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 sm:py-24 section-mist">
        <div className="max-w-xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="font-['Cormorant_Garamond'] text-3xl font-bold text-[hsl(var(--foreground))] mb-4">
            Not sure where to start?
          </h2>
          <p className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] mb-8">
            Send a note. If the Idea Clarity Session isn't the right fit, I'll tell you honestly.
          </p>
          <a href="mailto:emma@thecosmicsynthesist.com">
            <Button variant="outline" className="border-[rgba(201,168,76,0.28)] text-[hsl(var(--foreground))] hover:border-[rgba(201,168,76,0.55)] hover:bg-[rgba(27,77,68,0.16)] font-['Inter'] text-sm px-8 py-5 rounded-lg">
              Send a Note
            </Button>
          </a>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
